import type { AxiosResponse } from 'axios';
import type {
  Patient,
  FhirResource,
  MedicationRequest,
  Observation,
  Claim,
  Coverage,
  Condition,
  ServiceRequest,
  CarePlan,
  Bundle,
  BundleEntry,
  CareTeam,
  PlanDefinition,
  Provenance,
} from 'fhir/r4';
import type { Flag } from 'fhir/r4';
import { get, set } from 'lodash';
import { v4 as uuid } from 'uuid';

import * as CarePlanBuilder from '../common/fhir-builders/careplan';
import * as CareTeamBuilder from '../common/fhir-builders/careteam';
import type { CareTeamMember } from '../common/fhir-builders/careteam';
import * as FlagBuilder from '../common/fhir-builders/flag';
import * as ProvenanceBuilder from '../common/fhir-builders/provenance';
import * as ServcieRequestBuilder from '../common/fhir-builders/service-request';
import type FHIRProxyClient from '../common/fhirproxy-client';
import * as FHIRUtils from '../common/fhirutils';

//receive a referral
export async function receive(
  orgId: string,
  patient: Patient,
  conditions: Condition[],
  labs: Observation[],
  meds: MedicationRequest[],
  coverage: Coverage[],
  claims: Claim[],
  actorRef: { reference: string; display: string },
  fhirSender: FHIRProxyClient
) {
  try {
    const fhirBundle = buildReferralBundle(orgId, patient, conditions, labs, meds, coverage, claims, actorRef);

    const response = await fhirSender.send('post', '', fhirBundle);
    return response;
  } catch (error) {
    console.log('Error in referral:receive():', (error instanceof Error ? error.message : String(error)));
    throw error;
  }
}

//creates a new SDOH Service Request and CarePlan with high level activities
export async function launchCarePlan(
  orgId: string,
  patientId: string,
  planDefId: string,
  startDate: string,
  title: string,
  description: string,
  outreachAssociates: [{ reference: string; display: string }],
  registeredDietitians: [{ reference: string; display: string }],
  careCoordinators: [{ reference: string; display: string }],
  actorRef: { reference: string; display: string },
  updatedBy: string,
  fhirSender: FHIRProxyClient
) {
  try {
    //check if a careplan already exists before we launches careplan
    const carePlanResponse = await fhirSender.send<Bundle>('get', `CarePlan/?patient=${patientId}`, undefined);
    if ((carePlanResponse?.data?.total as number) > 0) {
      return;
    } else {
      const serviceRequest: ServiceRequest = ServcieRequestBuilder.buildServiceRequestForSDOHReferral(
        patientId,
        updatedBy
      );

      //Get PlanDefinition
      //Get PlanDefinition
      let response = await fhirSender.send<PlanDefinition>('get', 'PlanDefinition/' + planDefId, undefined);
      const planDef = response.data;

      //check if careTeam exists, if so, link with existing one, otherwise, creat a new careteam
      let careTeamResponse: AxiosResponse<Bundle> | Bundle | undefined;
      let careTeamId: string | undefined = undefined;
      let careTeam: CareTeam;

      if (!outreachAssociates) {
        //if none specified, use actor as part of the initial careTeam
        //create a CareTeam resource
        outreachAssociates = [actorRef];
      }

      careTeamResponse = await fhirSender.send<Bundle>('get', `CareTeam/?patient=${patientId}`, undefined);
      careTeamResponse = careTeamResponse?.data;
      if (((careTeamResponse as Bundle | undefined)?.total as number) > 0) {
        careTeam = (careTeamResponse as Bundle | undefined)?.entry?.[0]?.resource as CareTeam;
        careTeamId = careTeam?.id;
        //if existing careteam doesn't have WA OR RD info and it's available, update them
        if (!getCareTeamParticipant(careTeam, 'WELLNESS_ASSOCIATE_CODE') && outreachAssociates?.[0]) {
          setCareTeamParticipant(careTeam, 'WELLNESS_ASSOCIATE_CODE', outreachAssociates);
        }
        if (!getCareTeamParticipant(careTeam, 'DIETICIAN_CODE') && registeredDietitians?.[0]) {
          setCareTeamParticipant(careTeam, 'DIETICIAN_CODE', registeredDietitians);
        }
      } else {
        //careTeam doesn't exist, create a new careTeam
        careTeamId = uuid();
        careTeam = CareTeamBuilder.build(
          orgId,
          patientId,
          careTeamId,
          outreachAssociates,
          registeredDietitians,
          careCoordinators,
          updatedBy
        );
      }

      //create CarePlan bundle
      let goalId: string | undefined, conditionId: string | undefined; //these will be undefined for a new careplan
      let carePlanId = uuid();
      const newCarePlanBundle: Bundle = CarePlanBuilder.buildBundle(
        patientId,
        carePlanId,
        serviceRequest.id!,
        goalId,
        conditionId,
        planDef,
        careTeamId,
        startDate,
        title,
        description,
        updatedBy,
        orgId
      );
      const bundleEntries = newCarePlanBundle.entry!;

      const cpProvenancce: Provenance = ProvenanceBuilder.build(
        `CarePlan/${carePlanId}`,
        actorRef,
        patientId,
        'CREATE',
        'Care Plan Created',
        { code: orgId, display: orgId }
      );

      const srPprovenancce: Provenance = ProvenanceBuilder.build(
        `ServiceRequest/${serviceRequest.id}`,
        actorRef,
        patientId,
        'CREATE',
        'Referral',
        { code: orgId, display: orgId }
      );

      carePlanId = bundleEntries[0]!.resource!.id!;
      bundleEntries.push(FHIRUtils.buildResourceEntry(serviceRequest, false));
      bundleEntries.push(FHIRUtils.buildResourceEntry(careTeam, false));
      bundleEntries.push(FHIRUtils.buildResourceEntry(cpProvenancce, false));
      bundleEntries.push(FHIRUtils.buildResourceEntry(srPprovenancce, false));

      newCarePlanBundle.entry = bundleEntries;

      response = await fhirSender.send('post', '', newCarePlanBundle);
      return response;
    }
  } catch (error) {
    console.log('Error in referral:launchCarePlan():', (error instanceof Error ? error.message : String(error)));
    throw error;
  }
}

//assign new practitioners to an existing CareTeam
export async function updateCareTeam(
  fhirPatient: Patient,
  orgId: string,
  patientId: string,
  careTeamId: string | undefined,
  outreachAssociates: CareTeamMember[] | undefined,
  registeredDietitians: CareTeamMember[] | undefined,
  careCoordinators: CareTeamMember[] | undefined,
  actorRef: { reference: string; display: string },
  updatedBy: string,
  asignee: string,
  assigneeType: string, //OA,WA,RD,CC
  fhirSender: FHIRProxyClient
) {
  try {
    const fhirBundle: FhirResource = FHIRUtils.buildEmptyBundle('transaction');
    const bundleEntries: BundleEntry[] = [];
    if (!careTeamId) {
      careTeamId = uuid();
    }
    const careTeam: CareTeam = CareTeamBuilder.build(
      orgId,
      patientId,
      careTeamId,
      outreachAssociates,
      registeredDietitians,
      careCoordinators,
      updatedBy
    );
    bundleEntries.push(FHIRUtils.buildResourceEntry(careTeam, false));

    if (assigneeType !== 'CC') {
      //not create flag if assignning new CC
      const flagpayload: Flag = FlagBuilder.build(
        patientId,
        actorRef.reference.split('/')[1]!, //practitionerId
        { code: 'admin', display: 'administrative' },
        { code: 'ALERT', display: 'Alert' },
        'Assigned you to be part of the CareTeam by ' + updatedBy,
        updatedBy,
        orgId,
        asignee
      );
      bundleEntries.push(FHIRUtils.buildResourceEntry(flagpayload, false));
    }

    const provenance: Provenance = ProvenanceBuilder.build(
      `CareTeam/${careTeamId}`,
      actorRef,
      patientId,
      'UPDATE',
      'Care Team Updated',
      { code: orgId, display: orgId }
    );
    bundleEntries.push(FHIRUtils.buildResourceEntry(provenance, false));

    fhirBundle.entry = bundleEntries;
    const response = await fhirSender.send('post', '', fhirBundle);

    return response;
  } catch (error) {
    console.log('Error in referral:launchCarePlan():', (error instanceof Error ? error.message : String(error)));
    throw error;
  }
}

//creates a referral service reqeust for SDOH
export async function createSDOHReferralServiceRequest(
  orgId: string,
  patientId: string,
  actorRef: { reference: string; display: string },
  updatedBy: string,
  fhirSender: FHIRProxyClient
) {
  try {
    const fhirBundle: FhirResource = FHIRUtils.buildEmptyBundle('transaction');
    const bundleEntries: BundleEntry[] = [];

    const serviceRequest: ServiceRequest = ServcieRequestBuilder.buildServiceRequestForSDOHReferral(patientId, updatedBy);
    bundleEntries.push(FHIRUtils.buildResourceEntry(serviceRequest, false));

    const provenancce: Provenance = ProvenanceBuilder.build(
      `ServiceRequest/${serviceRequest.id}`,
      actorRef,
      patientId,
      'CREATE',
      'Referral',
      { code: orgId, display: orgId }
    );
    bundleEntries.push(FHIRUtils.buildResourceEntry(provenancce, false));

    fhirBundle.entry = bundleEntries;
    const response = await fhirSender.send('post', '', fhirBundle);

    return response;
  } catch (error) {
    console.log('Error in referral:createSDOHServiceRequest():', (error instanceof Error ? error.message : String(error)));
    throw error;
  }
}

//creates a new CarePlan with high level activities
export async function createNewCarePlan(
  orgId: string,
  patientId: string,
  serviceRequestId: string,
  planDefId: string,
  careTeamId: string, //can be null
  startDate: string,
  title: string,
  description: string,
  actorRef: { reference: string; display: string },
  updatedBy: string,
  fhirSender: FHIRProxyClient
) {
  try {
    //check if a careplan already exists before we launches careplan
    const carePlanResponse = await fhirSender.send<Bundle>('get', `CarePlan/?patient=${patientId}`, undefined);
    if ((carePlanResponse?.data?.total as number) > 0) {
      return;
    } else {
      //these will be undefined for a new careplan
      let carePlanId: string | undefined = undefined;
      const goalId: string | undefined = undefined;
      const conditionId: string | undefined = undefined;

      let response = await fhirSender.send<PlanDefinition>('get', 'PlanDefinition/' + planDefId, undefined);
      const planDef = response.data;
      const newCarePlanBundle: Bundle = CarePlanBuilder.buildBundle(
        patientId,
        carePlanId,
        serviceRequestId,
        goalId,
        conditionId,
        planDef,
        careTeamId,
        startDate,
        title,
        description,
        updatedBy,
        orgId
      );
      const bundleEntries = newCarePlanBundle.entry!;
      carePlanId = bundleEntries[0]!.resource!.id!;
      const provenancce: Provenance = ProvenanceBuilder.build(
        `CarePlan/${carePlanId}`,
        actorRef,
        patientId,
        'CERATE',
        'Care Plan Created',
        { code: orgId, display: orgId }
      );
      bundleEntries.push(FHIRUtils.buildResourceEntry(provenancce, false));

      newCarePlanBundle.entry = bundleEntries;
      response = await fhirSender.send('post', '', newCarePlanBundle);
      return response;
    }
  } catch (error) {
    console.log('Error in referral:createNewCarePlan():', (error instanceof Error ? error.message : String(error)));
    throw error;
  }
}

//assign a CareTeam to a CarePlan
export async function assignCareTeam(
  orgId: string,
  patientId: string,
  carePlanId: string,
  carePlan: CarePlan, //optional
  careTeamId: string,
  outreachAssociates: [{ reference: string; display: string }],
  registeredDietitians: [{ reference: string; display: string }],
  careCoordinators: [{ reference: string; display: string }],
  actorRef: { reference: string; display: string },
  updatedBy: string,
  fhirSender: FHIRProxyClient
) {
  try {
    const fhirBundle: FhirResource = FHIRUtils.buildEmptyBundle('transaction');
    const bundleEntries: BundleEntry[] = [];
    //Get CarePlan
    if (!carePlan) {
      const carePlanResponse = await fhirSender.send<CarePlan>('get', 'CarePlan/' + carePlanId, undefined);
      carePlan = carePlanResponse.data;
    }

    let careTeam: CareTeam;
    //check if careTeam exists, if so, link with existing one, otherwise, creat a new careteam
    let careTeamResponse: AxiosResponse<Bundle> | Bundle | undefined = await fhirSender.send<Bundle>(
      'get',
      `CareTeam/?patient=${patientId}`,
      undefined,
    );
    careTeamResponse = careTeamResponse?.data;
    if (((careTeamResponse as Bundle | undefined)?.total as number) > 0) {
      careTeam = (careTeamResponse as Bundle | undefined)?.entry?.[0]?.resource as CareTeam;
    } else {
      //careTeam doesn't exist, create a CareTeam
      careTeam = CareTeamBuilder.build(
        orgId,
        patientId,
        careTeamId,
        outreachAssociates,
        registeredDietitians,
        careCoordinators,
        updatedBy
      );
      bundleEntries.push(FHIRUtils.buildResourceEntry(careTeam, false));
    }

    carePlan.careTeam = [
      {
        reference: `CareTeam/${careTeam.id}`,
      },
    ];
    bundleEntries.push(FHIRUtils.buildResourceEntry(carePlan, false));

    const provenancce: Provenance = ProvenanceBuilder.build(
      `CarePlan/${carePlan.id!}`,
      actorRef,
      patientId,
      'UPDATE',
      'CareTeam Assigned',
      { code: orgId, display: orgId }
    );
    bundleEntries.push(FHIRUtils.buildResourceEntry(provenancce, false));

    fhirBundle.entry = bundleEntries;
    const response = await fhirSender.send('post', '', fhirBundle);

    return response;
  } catch (error) {
    console.log('Error in referral:assignCareTeam():', (error instanceof Error ? error.message : String(error)));
    throw error;
  }
}

function buildReferralBundle(
  orgId: string,
  patient: Patient,
  conditions: Condition[],
  labs: Observation[],
  meds: MedicationRequest[],
  coverage: Coverage[],
  claims: Claim[],
  actorRef: { reference: string; display: string }
): FhirResource {
  const fhirBundle: FhirResource = FHIRUtils.buildEmptyBundle('transaction');
  let bundleEntries: BundleEntry[] = [];

  bundleEntries.push(FHIRUtils.buildResourceEntry(patient, false));

  if (conditions && conditions.length > 0) {
    bundleEntries = bundleEntries.concat(FHIRUtils.buildResourceEntries(conditions, false));
  }
  if (labs && labs.length > 0) {
    bundleEntries = bundleEntries.concat(FHIRUtils.buildResourceEntries(labs, false));
  }
  if (meds && meds.length > 0) {
    bundleEntries = bundleEntries.concat(FHIRUtils.buildResourceEntries(meds, false));
  }
  if (coverage && coverage.length > 0) {
    bundleEntries = bundleEntries.concat(FHIRUtils.buildResourceEntries(coverage, false));
  }
  if (claims && claims.length > 0) {
    bundleEntries = bundleEntries.concat(FHIRUtils.buildResourceEntries(claims, false));
  }

  const provenancce: Provenance = ProvenanceBuilder.build(
    `Patient/${patient.id}`,
    actorRef,
    patient.id,
    'CREATE',
    'Patient Referral',
    { code: orgId, display: orgId }
  );
  bundleEntries.push(FHIRUtils.buildResourceEntry(provenancce, false));

  fhirBundle.entry = bundleEntries;

  return fhirBundle;
}

export enum CareTeam_RoleCode {
  DIETICIAN_CODE = '159033005',
  WELLNESS_ASSOCIATE_CODE = '309396002',
  CARE_COORDINATOR = '768820003',
}

export enum CareTeam_Display {
  DIETICIAN_CODE = 'Dietitian',
  WELLNESS_ASSOCIATE_CODE = 'Associate specialist',
  CARE_COORDINATOR = 'Care coordinator',
}

type CareTeamRoleKey = keyof typeof CareTeam_RoleCode;
//return participant info object
const getCareTeamParticipant = (careTeam: CareTeam, role: CareTeamRoleKey) => {
  let participant = careTeam?.['participant'] ?? [];
  participant = participant.filter((p) => {
    return get(p, 'role[0].coding[0].code') === CareTeam_RoleCode[role];
  });
  return participant?.[0]?.['member'];
};

//update participant info
const setCareTeamParticipant = (
  careTeam: CareTeam,
  role: CareTeamRoleKey,
  practitioner: CareTeamMember[] | undefined
) => {
  removeParticipant(careTeam, role);
  const newParticipant = {
    member: practitioner?.[0],
    role: [
      {
        coding: [
          {
            code: CareTeam_RoleCode[role],
            display: CareTeam_Display[role],
            system: 'http://snomed.info/sct',
          },
        ],
      },
    ],
  };
  const participantArray = careTeam?.['participant'] ?? [];
  participantArray.push(newParticipant);
  return set(careTeam, 'participant', participantArray);
};

const removeParticipant = (careTeam: CareTeam, role: CareTeamRoleKey) => {
  let participant = careTeam?.['participant'] ?? [];
  participant = participant.filter((p) => {
    return get(p, 'role[0].coding[0].code') !== CareTeam_RoleCode[role];
  });
  return set(careTeam, 'participant', participant);
};
